"use client"

import type { ReactNode } from "react"

import { TestChat as EmailTestChat } from "@/components/agents/email/dialogs/test-chat-dialog"
import { TestChat as MessagingTestChat } from "@/components/agents/messaging/settings/test-chat-dialog"
import { useOnboarding } from "@/components/onboarding/onboarding-context"
import { OnboardingStepActions } from "@/components/onboarding/onboarding-step-actions"
import { VoiceAgentTestExperience } from "@/components/onboarding/voice-agent-test-experience"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { normalizeSignupOnboardingTestQuestions } from "@/lib/onboarding/prompt-personalization"
import { advanceSignupOnboardingState } from "@/lib/onboarding/signup-onboarding-steps"
import { useTranslations } from "next-intl"


export function TestStep() {
  const tUi = useTranslations("Onboarding.StepsTestStep")

  const { state, currentAgent, saveState, saving, messagingAgent, emailAgent } =
    useOnboarding()

  // Tools stay off outside the Shopify flow: they exist here so a connected store can be
  // questioned about its catalogue, and the ordinary onboarding never needed them.
  const storeConnected = Boolean(state.shopify_shop_domain)

  if (!currentAgent?.agent_id) {
    return (
      <div className="space-y-6">
        <Alert className="border-destructive/40">
          <AlertDescription>
            <span key={tUi("createThisAgentBeforeTestingItUseBack")}>{tUi("createThisAgentBeforeTestingItUseBack")}</span></AlertDescription>
        </Alert>
        <OnboardingStepActions />
      </div>
    )
  }

  let experience: ReactNode
  const openingMessage = currentAgent.opening_message ?? "Hello! How can I help?"
  const suggestedQuestions = normalizeSignupOnboardingTestQuestions({
    agentType: currentAgent.type,
    roleNames: currentAgent.role_names,
    questions: currentAgent.suggested_questions ?? [],
  })
  switch (currentAgent.type) {
    case "email":
      experience = (
        <EmailTestChat
          agentId={currentAgent.agent_id}
          agentName={currentAgent.name}
          useAiTools={storeConnected && readUseAiTools(emailAgent, "auto_reply")}
          experience={{
            kind: "onboarding",
            openingMessage,
            suggestedQuestions,
          }}
        />
      )
      break
    case "messaging":
      experience = (
        <MessagingTestChat
          agentId={currentAgent.agent_id}
          agentName={currentAgent.name}
          useAiTools={
            storeConnected && (messagingAgent?.config.reply?.use_ai_tools ?? false)
          }
          experience={{
            kind: "onboarding",
            openingMessage,
            suggestedQuestions,
          }}
        />
      )
      break
    case "voice":
      experience = (
        <VoiceAgentTestExperience
          agentId={currentAgent.agent_id}
          agentName={currentAgent.name}
        />
      )
      break
    default: {
      const exhaustiveType: never = currentAgent.type
      return exhaustiveType
    }
  }

  return (
    <div className="space-y-6">
      {experience}
      <OnboardingStepActions>
        <Button
          type="button"
          disabled={saving}
          onClick={() =>
            void saveState(advanceSignupOnboardingState(state))
          }
        >
          <span key={tUi("continueToChannelConnection")}>{tUi("continueToChannelConnection")}</span></Button>
      </OnboardingStepActions>
    </div>
  )
}

/** The email agent reaches the context untyped; read its switch without trusting the shape. */
function readUseAiTools(agent: unknown, block: "auto_reply"): boolean {
  if (typeof agent !== "object" || agent === null) return false
  const config = (agent as { config?: unknown }).config
  if (typeof config !== "object" || config === null) return false
  const settings = (config as Record<string, unknown>)[block]
  if (typeof settings !== "object" || settings === null) return false
  return (settings as { use_ai_tools?: unknown }).use_ai_tools === true
}
